import MuiSwitch, { SwitchProps } from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';

export interface CustomSwitchProps extends SwitchProps {
    /** If `true`, component includes checked and cross icons */
    withIcon?: boolean;
}
export const Switch = styled(({ withIcon, className, ...props }: CustomSwitchProps) => (
    <MuiSwitch
        focusVisibleClassName=".Mui-focusVisible"
        className={clsx(className, withIcon && 'with-icon')}
        disableRipple
        {...props}
    />
))(({ theme }) => ({
    width: 60,
    height: 40,
    padding: 4,
    margin: 0,
    '& .MuiSwitch-switchBase': {
        padding: 12,
        color: theme.palette.grey[50],
        transition: theme.transitions.create(['color', 'transform'], {
            duration: 200,
        }),
        '& .MuiSwitch-thumb': {
            width: 16,
            height: 16,
            transform: 'scale(1)',
            transition: theme.transitions.create(['width', 'height', 'transform'], {
                duration: 200,
            }),
        },
        '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: theme.palette.common.white,
            '& + .MuiSwitch-track': {
                opacity: 1,
                border: `2px solid transparent`,
            },
            '& .MuiSwitch-thumb': {
                transform: 'scale(1.5)',
            },
        },
        '&:hover': {
            color: theme.palette.grey[300],
            '&.Mui-checked': {
                color: theme.palette.common.white,
            },
        },
        '&:active': {
            color: theme.palette.grey[300],
            '& .MuiSwitch-thumb': {
                transform: 'scale(1.75)',
            },
            '&.Mui-checked': {
                color: theme.palette.common.white,
                '& .MuiSwitch-thumb': {
                    transform: 'scale(1.75)',
                },
            },
        },
        // disabled
        '&.Mui-disabled': {
            color: theme.palette.grey[400],
            '& .MuiSwitch-thumb': {
                boxShadow: 'none',
            },
            '&.Mui-checked': {
                '&+.MuiSwitch-track': {
                    backgroundColor: theme.palette.grey[400],
                },
                '& .MuiSwitch-thumb': {
                    color: theme.palette.common.white,
                },
            },
        },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: theme.palette.primary.main,
        border: `6px solid ${theme.palette.common.white}`,
    },
    '& .MuiSwitch-track': {
        borderRadius: 32 / 2,
        border: `2px solid ${theme.palette.grey[500]}`,
        backgroundColor: theme.palette.grey[900],
        opacity: 1,
        transition: theme.transitions.create(['background-color', 'border']),
    },
    // size small
    '&.MuiSwitch-sizeSmall': {
        width: 45,
        height: 30,
        padding: 3,
        '& .MuiSwitch-switchBase': {
            padding: 9,
            '& .MuiSwitch-thumb': {
                width: 12,
                height: 12,
            },
            '&.Mui-checked': {
                transform: 'translateX(15px)',
            },
        },
    },
    // icons
    '&.with-icon': {
        '& .MuiSwitch-switchBase': {
            padding: 8,
            '& .MuiSwitch-thumb': {
                width: 24,
                height: 24,
            },
        },
        '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
            transform: 'scale(1)',
        },
        '& .MuiSwitch-switchBase:active': {
            '& .MuiSwitch-thumb, &.Mui-checked .MuiSwitch-thumb': {
                transform: 'scale(1.167)',
            },
        },
        '& .MuiSwitch-switchBase .MuiSwitch-thumb:before': {
            transform: 'scale(1)',
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_14463_236554)'%3E%3Cpath d='M12.7005 3.80665C12.4405 3.54665 12.0205 3.54665 11.7605 3.80665L8.50047 7.05998L5.24047 3.79998C4.98047 3.53998 4.56047 3.53998 4.30047 3.79998C4.04047 4.05998 4.04047 4.47998 4.30047 4.73998L7.56047 7.99998L4.30047 11.26C4.04047 11.52 4.04047 11.94 4.30047 12.2C4.56047 12.46 4.98047 12.46 5.24047 12.2L8.50047 8.93998L11.7605 12.2C12.0205 12.46 12.4405 12.46 12.7005 12.2C12.9605 11.94 12.9605 11.52 12.7005 11.26L9.44047 7.99998L12.7005 4.73998C12.9538 4.48665 12.9538 4.05998 12.7005 3.80665Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_14463_236554'%3E%3Crect width='16' height='16' fill='white' transform='translate(0.5)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`,
        },
        '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb:before': {
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_9190_185055)'%3E%3Cpath d='M6.49911 10.7799L4.18578 8.46655C3.92578 8.20655 3.50578 8.20655 3.24578 8.46655C2.98578 8.72655 2.98578 9.14655 3.24578 9.40655L6.03245 12.1932C6.29245 12.4532 6.71245 12.4532 6.97245 12.1932L14.0258 5.13988C14.2858 4.87988 14.2858 4.45988 14.0258 4.19988C13.7658 3.93988 13.3458 3.93988 13.0858 4.19988L6.49911 10.7799Z' fill='%23053B51'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_9190_185055'%3E%3Crect width='16' height='16' fill='white' transform='translate(0.5)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`,
        },
        '&.MuiSwitch-sizeSmall': {
            '& .MuiSwitch-switchBase': {
                padding: 6,
                '& .MuiSwitch-thumb': {
                    width: 18,
                    height: 18,
                },
                '&.Mui-checked': {
                    transform: 'translateX(15px)',
                },
            },
        },
        '& .Mui-disabled': {
            color: theme.palette.grey[800],
            '& .MuiSwitch-thumb:before': {
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='17' height='16' viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_9190_185129)'%3E%3Cpath d='M12.7005 3.80665C12.4405 3.54665 12.0205 3.54665 11.7605 3.80665L8.50047 7.05998L5.24047 3.79998C4.98047 3.53998 4.56047 3.53998 4.30047 3.79998C4.04047 4.05998 4.04047 4.47998 4.30047 4.73998L7.56047 7.99998L4.30047 11.26C4.04047 11.52 4.04047 11.94 4.30047 12.2C4.56047 12.46 4.98047 12.46 5.24047 12.2L8.50047 8.93998L11.7605 12.2C12.0205 12.46 12.4405 12.46 12.7005 12.2C12.9605 11.94 12.9605 11.52 12.7005 11.26L9.44047 7.99998L12.7005 4.73998C12.9538 4.48665 12.9538 4.05998 12.7005 3.80665Z' fill='%23E1E2E6'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_9190_185129'%3E%3Crect width='16' height='16' fill='white' transform='translate(0.5)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`,
            },
            '&.Mui-checked .MuiSwitch-thumb:before': {
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='17' height='16' viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_9190_185078)'%3E%3Cpath d='M6.49911 10.7799L4.18578 8.46655C3.92578 8.20655 3.50578 8.20655 3.24578 8.46655C2.98578 8.72655 2.98578 9.14655 3.24578 9.40655L6.03245 12.1932C6.29245 12.4532 6.71245 12.4532 6.97245 12.1932L14.0258 5.13988C14.2858 4.87988 14.2858 4.45988 14.0258 4.19988C13.7658 3.93988 13.3458 3.93988 13.0858 4.19988L6.49911 10.7799Z' fill='%23C5C8CA'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_9190_185078'%3E%3Crect width='16' height='16' fill='white' transform='translate(0.5)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`,
            },
        },
    },
}));
